<template>
    <div>
        <b-modal
            id="createMedicalNoteModal"
            ref="createMedicalNoteModal"
            :lazy="true"
            title-tag="h2"
            title="New Medical Note"
        >
            <form class="form-horizontal">
                <div class="form-group row" v-if="!hidePatientField">
                    <label for="patient" class="col-sm-3 col-form-label"
                        >Patient:</label
                    >
                    <div class="col-sm-9">
                        <v-select
                            id="patient_id"
                            :options="patient_options_exclude"
                            v-validate="'required'"
                            :class="{
                                'border-danger': errors.has('patient_id'),
                                rounded: true
                            }"
                            v-model="data.patient"
                            @search="searchPatient"
                            @input="patientSelected"
                            placeholder="Patient name (required)"
                            name="patient_id"
                            label="fullname"
                        >
                            <template slot="no-options">
                                <span>Type to search</span>
                            </template>
                        </v-select>
                    </div>
                </div>
                <div class="form-group row">
                    <label for="template" class="col-sm-3 col-form-label"
                        >Template:</label
                    >
                    <div class="col-sm-9">
                        <select
                            id="soap"
                            v-model="data.soap_id"
                            class="form-control"
                        >
                            <option
                                v-for="soap in sortedSoaps"
                                :key="soap.id"
                                :value="soap.id"
                                v-html="soap.name"
                            ></option>
                        </select>
                    </div>
                </div>
                <div class="form-group row">
                    <label for="date" class="col-sm-3 col-form-label"
                        >Date:</label
                    >
                    <div class="col-sm-9">
                        <date-picker
                            :config="datePickerOptions"
                            v-model="data.note_date"
                            :value="data.note_date"
                            v-validate="'required'"
                            class="form-control"
                            :class="{
                                'border-danger': errors.has('date')
                            }"
                            name="date"
                            placeholder="Date (required)"
                        ></date-picker>
                    </div>
                </div>
                <div class="form-group row">
                    <label for="complaint" class="col-sm-3 col-form-label"
                        >Complaint:</label
                    >
                    <div class="col-sm-9">
                        <input
                            id="complaint"
                            v-model="data.complaint"
                            type="text"
                            class="form-control"
                            name="complaint"
                            placeholder="Complaint (optional)"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="referral" class="col-sm-3 col-form-label"
                        >Referred By:</label
                    >
                    <div class="col-sm-9">
                        <vet-select
                            v-model="data.referrer"
                            @input="referrerChange"
                            v-validate
                            module="referrer"
                            placeholder="Select referral source (optional)"
                            name="referrer_id"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="provider" class="col-sm-3 col-form-label"
                        >Provider:</label
                    >
                    <div class="col-sm-9">
                        <v-select
                            id="staffs"
                            ref="vselect"
                            :options="staff_options_exclude"
                            v-model="data.staffs"
                            :label="'fullname'"
                            placeholder="Provider (optional)"
                        />
                    </div>
                </div>
            </form>
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading"
                    @click="$refs.createMedicalNoteModal.hide()"
                    class="float-left"
                    variant="secondary"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img v-if="loading" src="assets/ajax-loader.gif" />
                    <b-button @click="save" v-else variant="primary"
                        >Save</b-button
                    >
                </div>
            </div>
        </b-modal>
    </div>
</template>
<style></style>
<script>
import _ from "lodash";
import vSelect from "vue-select";
import vetSelect from "../commons/vet-select.vue";
export default {
    components: {
        vetSelect,
        vSelect
    },
    props: {
        hidePatientField: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            data: {
                isShortcut: 1,
                id: null,
                client: null,
                patient: null,
                staffs: null,
                soap_id: null,
                referrer: null,
                referral_id: null,
                date: null,
                note_date: null,
                complaint: null
            },
            pOptions: [],
            soaps: [],
            staffList: [],
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            basic_soap: [
                { id: "a", name: "Basic SOAP" },
                { id: "b", name: "Simple SOAP" },
                { id: "c", name: "Standard SOAP" }
            ],
            loading: false
        };
    },
    computed: {
        staff_options_exclude() {
            return _.difference(this.staffList, this.data.staffs);
        },
        patient_options_exclude() {
            return _.differenceWith(
                this.pOptions,
                this.data.patient,
                (itemA, itemB) => itemA.id == itemB.id
            );
        },
        sortedSoaps() {
            return _.sortBy(this.soaps, ["name"]);
        }
    },
    watch: {
        "data.patient": {
            handler(newData) {
                if (newData) {
                    this.getReferrer(newData.id).then(referrer => {
                        this.data.referrer = referrer[0];
                        this.data.referral_id = this._bindReferrer(
                            this.data.referrer
                        );
                    });
                } else {
                    this.data.referral_id = null;
                    this.data.referrer = null;
                }
            }
        },
        sortedSoaps: {
            handler(newData) {
                if (_.isArray(newData) && newData.length > 0) {
                    this.data.soap_id = newData[0].id;
                }
            }
        }
    },
    methods: {
        open(params = {}) {
            this.datePickerOptions.format = this.getDateFormat();

            this.data.date = moment();
            this.data.note_date = moment();

            this.getStaffList();
            this.getSoap();

            this.$refs.createMedicalNoteModal.show();
            this.$refs.createMedicalNoteModal.$validator.reset();

            if (params.patientId) {
                this.data.patient = {
                    id: params.patientId
                };
            }
        },
        dateChange(event, element) {
            this.data[element] = event.date;
        },
        referrerChange(referrer) {
            if (referrer) {
                this.data.referrer = referrer;
                this.data.referral_id = this._bindReferrer(referrer);
            } else {
                this.data.referrer = null;
                this.data.referral_id = null;
            }
        },
        getReferrer() {
            let self = this;

            return this.$http
                .get(
                    process.env.ACTOR_SERVICE +
                        "search-referral-by-patient?id=" +
                        _.get(self, "data.patient.id", "")
                )
                .then(response => response.data.response.resources);
        },
        getStaffList() {
            let self = this;
            this.$http
                .get(process.env.ACTOR_SERVICE + "staff?provider=1")
                .then(function (response) {
                    self.staffList = response.data.response.resources;
                });
        },
        getSoap() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/soap")
                .then(function (response) {
                    self.soaps = response.data.response.resources;

                    self.data.soap_id = self.soaps[0].id;
                });
        },
        searchPatient(search) {
            if (search && search.length > 2) {
                return this.searchPatientResource(search);
            }
        },
        searchPatientResource(search) {
            let self = this;

            this.$http
                .get(
                    process.env.PATIENT_SERVICE +
                        "search-patient?query=" +
                        search
                )
                .then(function (response) {
                    if (_.get(response, "data.response.resources")) {
                        self.pOptions = response.data.response.resources;
                    }
                });
        },
        patientSelected(event) {
            let self = this;
            if (event && event.length < 1) {
                return;
            }
            if (self.data.patient > 1 && self.pOptions.length) {
                return;
            }
            let params = "";
            return this.searchPatientResource(params);
        },
        async save() {
            let self = this;
            let valid = null;
            let response = null;
            self.loading = true;

            valid = await self.$validator.validateAll();

            if (valid) {
                const now = moment().tz(self._timeZone);
                // moment() will parse the date and set the time to 12AM
                // use now to set to time now
                self.data.note_date = moment(
                    self.data.note_date,
                    self.getDateFormat()
                ).set({
                    hour: now.hour(),
                    minute: now.minutes(),
                    second: now.second()
                });
                response = await this._saveMedicalNote(self.data);

                if (response && response.status == 0) {
                    self.loading = false;

                    if (response.id) {
                        window.location =
                            process.env.APPS +
                            "index.php/soap/soap/view/" +
                            response.id;
                    }

                    self.$refs.createMedicalNoteModal.hide();
                    self._message(response.message);
                } else {
                    self.loading = false;
                    self._message(response.message, "danger");
                }
            } else {
                self.loading = false;
                return false;
            }
        }
    }
};
</script>
